import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Breadcrumbs from "@/components/vr/Breadcrumbs";
import Seo, { breadcrumbLd } from "@/components/vr/Seo";
import { ChevronRight } from "lucide-react";

export function ClassesList() {
  const [classes, setClasses] = useState([]);
  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)); }, []);
  return (
    <div className="vr-container my-8">
      <Seo
        title="All Classes — Bihar Board 9 to 12"
        description="बिहार बोर्ड कक्षा 9, 10, 11 एवं 12 के लिए निःशुल्क Notes, MCQ, महत्वपूर्ण प्रश्न, Online Tests एवं YouTube Classes।"
        canonical="/classes"
        jsonLd={breadcrumbLd([{ name: "Home", url: "/" }, { name: "Classes", url: "/classes" }])}
      />
      <Breadcrumbs items={[{ label: "Classes" }]} />
      <h1 className="font-display font-extrabold text-3xl text-[#0F172A]">All Classes — Bihar Board</h1>
      <p className="text-slate-600 mt-2 font-hindi">बिहार बोर्ड कक्षा 9 से 12 तक के सम्पूर्ण अध्ययन सामग्री।</p>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {classes.map((c) => (
          <Link key={c.id} to={`/${c.slug}`} className="vr-card p-5" data-testid={`cls-${c.slug}`}>
            <div className="w-12 h-12 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center font-black text-xl">{c.name.replace("Class ", "")}</div>
            <h2 className="mt-3 font-display font-bold text-lg text-[#0F172A]">{c.name}</h2>
            <div className="text-sm text-slate-500 font-hindi">{c.hindi_name}</div>
            <p className="text-xs text-slate-500 mt-2 line-clamp-2">{c.description}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ClassDetail() {
  const params = useParams();
  const { pathname } = useLocation();
  // Support both /classes/:slug and short /class-10 style URLs.
  const slug = params.slug || pathname.replace(/^\//, "").split("/")[0];
  const [cls, setCls] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [notes, setNotes] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    api.get(`/classes/${slug}`).then((r) => { setCls(r.data); }).catch(() => {});
    api.get(`/subjects?class_slug=${slug}`).then((r) => setSubjects(r.data));
    api.get(`/notes?class_slug=${slug}&limit=6`).then((r) => setNotes(r.data));
    api.get(`/quizzes?class_slug=${slug}`).then((r) => setQuizzes(r.data));
    api.get(`/videos?class_slug=${slug}&limit=6`).then((r) => setVideos(r.data));
  }, [slug]);

  if (!cls) return <div className="vr-container my-10 text-slate-500">Loading...</div>;
  const canonical = `/${slug}`;
  const title = cls.meta_title || `${cls.name} — Bihar Board Notes, MCQ, Videos & Tests`;
  const desc = cls.meta_description || `${cls.name} (${cls.hindi_name || ""}) की सम्पूर्ण Bihar Board तैयारी — Chapter-wise Notes, MCQ Quiz, Important Questions एवं YouTube Classes।`;
  return (
    <div className="vr-container my-8">
      <Seo
        title={title}
        description={desc}
        canonical={canonical}
        jsonLd={breadcrumbLd([{ name: "Home", url: "/" }, { name: "Classes", url: "/classes" }, { name: cls.name, url: canonical }])}
      />
      <Breadcrumbs items={[{ to: "/classes", label: "Classes" }, { label: cls.name }]} />
      <div className="vr-card p-6">
        <h1 className="font-display font-extrabold text-3xl text-[#0F172A]">{cls.name}</h1>
        <div className="text-slate-500 font-hindi text-lg">{cls.hindi_name}</div>
        <p className="mt-2 text-slate-600 font-hindi">{cls.description}</p>
      </div>

      <SectionBlock title="Subjects" hindi="विषय">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {subjects.map((s) => (
            <Link key={s.id} to={`/subjects/${s.slug}?class=${slug}`} className="vr-card p-4" data-testid={`cls-sub-${s.slug}`}>
              <h3 className="font-display font-bold text-[#0F172A] text-base">{s.name}</h3>
              <div className="text-xs text-slate-500 font-hindi">{s.hindi_name}</div>
            </Link>
          ))}
        </div>
      </SectionBlock>

      <SectionBlock title="Notes" hindi="नोट्स">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {notes.map((n) => (
            <Link key={n.id} to={`/notes/${n.slug}`} className="vr-card p-4" data-testid={`cls-note-${n.slug}`}>
              <h3 className="font-semibold text-[#0F172A] text-base">{n.title}</h3>
              <div className="text-xs text-slate-500 mt-1 font-hindi line-clamp-2">{n.intro}</div>
            </Link>
          ))}
          {notes.length === 0 && <div className="text-slate-500 text-sm col-span-3">जल्द ही अपलोड होंगे।</div>}
        </div>
      </SectionBlock>

      <SectionBlock title="MCQ Quizzes" hindi="क्विज़">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {quizzes.map((q) => (
            <Link key={q.id} to={`/quiz/${q.slug}`} className="vr-card p-4" data-testid={`cls-quiz-${q.slug}`}>
              <h3 className="font-semibold text-[#0F172A] text-base">{q.title}</h3>
              <div className="text-xs text-slate-500 mt-1 font-hindi">{q.duration_minutes} मिनट</div>
            </Link>
          ))}
          {quizzes.length === 0 && <div className="text-slate-500 text-sm col-span-3">जल्द ही अपलोड होंगे।</div>}
        </div>
      </SectionBlock>

      <SectionBlock title="YouTube Classes" hindi="वीडियो">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {videos.map((v) => (
            <a key={v.id} href={`https://youtu.be/${v.youtube_id}`} target="_blank" rel="noreferrer" className="vr-card overflow-hidden" data-testid={`cls-video-${v.id}`}>
              <img loading="lazy" src={`https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`} alt={`${v.title} — YouTube video by VR Classes`} className="w-full aspect-video object-cover" />
              <h3 className="p-3 text-sm font-semibold line-clamp-2">{v.title}</h3>
            </a>
          ))}
          {videos.length === 0 && <div className="text-slate-500 text-sm col-span-3">जल्द ही अपलोड होंगे।</div>}
        </div>
      </SectionBlock>
    </div>
  );
}

function SectionBlock({ title, hindi, children }) {
  return (
    <section className="mt-8">
      <div className="mb-3">
        <div className="text-xs font-bold text-[#DC2626] uppercase">{hindi}</div>
        <h2 className="font-display font-extrabold text-xl text-[#0F172A]">{title} <ChevronRight className="inline" size={18} /></h2>
      </div>
      {children}
    </section>
  );
}
